import {
  Badge,
  Box,
  Button,
  HStack,
  Spinner,
  Tab,
  TabList,
  Tabs,
  Text,
  useDisclosure,
  WhiteCard,
} from '@elkaso-app/web-design';
import { useGetSiSupplierOverviewApi } from 'apis/supplier-intelligence';
import { useEffect, useMemo, useState } from 'react';
import { FiArrowLeft } from 'react-icons/fi';
import { Redirect, Route, Switch, useHistory, useLocation, useParams, useRouteMatch } from 'react-router-dom';
import { changeTitle } from 'redux/actions';
import { useAppDispatch } from 'redux/hooks';
import { OrdersTab } from '../components/orders-tab';
import { OverviewTab } from '../components/overview-tab';
import { ProductCatalogTab } from '../components/product-catalog-tab';
import { ProductIntelligenceDrawer } from '../components/product-intelligence-drawer';

const TAB_ROUTES = ['overview', 'orders', 'catalog'] as const;

const SupplierDetailScreen = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const location = useLocation();
  const match = useRouteMatch();
  const { id } = useParams<{ id: string }>();
  const supplierId = Number(id);
  const { data: overview, isLoading } = useGetSiSupplierOverviewApi(supplierId);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedProductName, setSelectedProductName] = useState<string | null>(null);
  const [selectedProductIds, setSelectedProductIds] = useState<number[]>([]);

  const tabIndex = useMemo(() => {
    const segment = location.pathname.split('/').pop();
    const idx = TAB_ROUTES.indexOf(segment as any);
    return idx >= 0 ? idx : 0;
  }, [location.pathname]);

  useEffect(() => {
    dispatch(changeTitle(overview?.profile?.name || 'Supplier Intelligence'));
  }, [dispatch, overview]);

  if (isLoading) {
    return (
      <WhiteCard p='lg'>
        <Spinner />
      </WhiteCard>
    );
  }

  if (!overview) {
    return (
      <WhiteCard p='lg'>
        <Text>Supplier not found</Text>
      </WhiteCard>
    );
  }

  const { profile, contacts } = overview;

  const handleProductClick = (productName: string, productIds: number[]) => {
    setSelectedProductName(productName);
    setSelectedProductIds(productIds);
    onOpen();
  };

  const handleTabChange = (index: number) => {
    history.push(`${match.url}/${TAB_ROUTES[index]}`);
  };

  const parentPath = match.url.replace(/\/[^/]+$/, '');

  return (
    <>
      <WhiteCard p='lg'>
        <HStack justify='space-between' mb={6}>
          <HStack spacing={4}>
            <Button leftIcon={<FiArrowLeft />} variant='ghost' onClick={() => history.push(parentPath)}>
              Back
            </Button>
            <Text fontSize='xl' fontWeight='bold'>
              {profile.name}
            </Text>
            <Badge colorScheme={profile.status === 'active' ? 'green' : 'gray'} fontSize='sm' px={3} py={1}>
              {profile.status}
            </Badge>
          </HStack>
        </HStack>

        <Tabs index={tabIndex} onChange={handleTabChange}>
          <TabList>
            <Tab>Overview</Tab>
            <Tab>Orders</Tab>
            <Tab>Catalog</Tab>
          </TabList>
        </Tabs>

        <Box p={4}>
          <Switch>
            <Route exact path={`${match.url}/overview`}>
              <OverviewTab profile={profile} contacts={contacts} />
            </Route>
            <Route exact path={`${match.url}/orders`}>
              <OrdersTab supplierId={supplierId} />
            </Route>
            <Route exact path={`${match.url}/catalog`}>
              <ProductCatalogTab supplierId={supplierId} onProductClick={handleProductClick} />
            </Route>
            <Redirect from={match.url} to={`${match.url}/overview`} />
          </Switch>
        </Box>
      </WhiteCard>

      <ProductIntelligenceDrawer
        isOpen={isOpen}
        onClose={onClose}
        productName={selectedProductName}
        productIds={selectedProductIds}
        supplierId={profile.id}
        supplierName={profile.name}
      />
    </>
  );
};

export default SupplierDetailScreen;
